// The components that are only markup: shadcn's Button, Badge, Card, Input,
// Textarea, Label, Separator, Kbd, Alert, Skeleton, Table, Breadcrumb and
// Resizable's handle, each a class from ui.css and its variants as data
// attributes. They render the same on the server as in the browser, so a
// page built ahead needs no script for them. Each takes class or className
// (Radix's children are given the latter) and passes the rest to its element.
import { toChildArray, type ComponentChildren, type JSX } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";
// Slot from its own package, not radix-ui's index: a page that draws only
// these components then carries none of the overlays' code.
import * as Slot from "@radix-ui/react-slot";
import { cn } from "../cn";
import { ChevronRight } from "../icons";

type Classy = { class?: string; className?: string };
type El<T extends keyof JSX.IntrinsicElements> = JSX.IntrinsicElements[T] & Classy;
const cls = (base: string, p: Classy) => cn(base, p.class as string, p.className);
function rest<T extends Classy>(p: T): Omit<T, "class" | "className"> {
  const { class: _c, className: _n, ...r } = p;
  return r;
}

export type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
export type ButtonSize = "default" | "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm";
export type ButtonProps = El<"button"> & { variant?: ButtonVariant; size?: ButtonSize; asChild?: boolean };

// Radix's Slot merges its child's className but not its class, and Preact
// writes the merged className over that class: fold the child's in first.
function slotted(base: string, p: Classy & { children?: ComponentChildren }) {
  const child = toChildArray(p.children)[0] as any;
  return cn(cls(base, p), child?.props?.class, child?.props?.className);
}

// asChild draws the button's look on its one child (a link, say), as shadcn's does.
export function Button({ variant = "default", size = "default", asChild, ...p }: ButtonProps) {
  const Comp: any = asChild ? Slot.Root : "button";
  return <Comp data-slot="button" data-variant={variant} data-size={size} className={asChild ? slotted("ui-button", p) : cls("ui-button", p)} type={asChild ? undefined : (p.type ?? "button")} {...rest(p)} />;
}

export type BadgeVariant = "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "brand";
export function Badge({ variant = "default", asChild, ...p }: El<"span"> & { variant?: BadgeVariant; asChild?: boolean }) {
  const Comp: any = asChild ? Slot.Root : "span";
  return <Comp data-slot="badge" data-variant={variant} className={asChild ? slotted("ui-badge", p) : cls("ui-badge", p)} {...rest(p)} />;
}

// A standing state, drawn as a dot: ok, off, error, or nothing yet.
export function Dot({ state, ...p }: El<"i"> & { state?: "ok" | "off" | "error" | "" }) {
  return <i data-slot="dot" data-state={state || undefined} className={cls("ui-dot", p)} aria-hidden="true" {...rest(p)} />;
}

export const Card = (p: El<"div">) => <div data-slot="card" className={cls("ui-card", p)} {...rest(p)} />;
export const CardHeader = (p: El<"div">) => <div data-slot="card-header" className={cls("ui-card-header", p)} {...rest(p)} />;
export const CardTitle = ({ as: As = "h3", ...p }: El<"h3"> & { as?: "h1" | "h2" | "h3" | "h4" | "div" }) => <As data-slot="card-title" className={cls("ui-card-title", p)} {...(rest(p) as any)} />;
export const CardDescription = (p: El<"p">) => <p data-slot="card-description" className={cls("ui-card-description", p)} {...rest(p)} />;
export const CardAction = (p: El<"div">) => <div data-slot="card-action" className={cls("ui-card-action", p)} {...rest(p)} />;
export const CardContent = (p: El<"div">) => <div data-slot="card-content" className={cls("ui-card-content", p)} {...rest(p)} />;
export const CardFooter = (p: El<"div">) => <div data-slot="card-footer" className={cls("ui-card-footer", p)} {...rest(p)} />;

export const Input = (p: El<"input">) => <input data-slot="input" className={cls("ui-input", p)} type={p.type ?? "text"} {...(rest(p) as any)} />;
export const Textarea = (p: El<"textarea">) => <textarea data-slot="textarea" className={cls("ui-textarea", p)} {...rest(p)} />;
// shadcn's NativeSelect: the browser's own <select>, drawn as an input, for
// a short list of choices that needs no menu of its own.
export const NativeSelect = (p: El<"select">) => <select data-slot="native-select" className={cls("ui-native-select", p)} {...(rest(p) as any)} />;
export const Label = (p: El<"label">) => <label data-slot="label" className={cls("ui-label", p)} {...rest(p)} />;

export function Separator({ orientation = "horizontal", decorative = true, ...p }: El<"div"> & { orientation?: "horizontal" | "vertical"; decorative?: boolean }) {
  return <div data-slot="separator" data-orientation={orientation} role={decorative ? "none" : "separator"} aria-orientation={decorative ? undefined : orientation} className={cls("ui-separator", p)} {...rest(p)} />;
}

export const Kbd = (p: El<"kbd">) => <kbd data-slot="kbd" className={cls("ui-kbd", p)} {...rest(p)} />;
export const Code = (p: El<"code">) => <code data-slot="code" className={cls("ui-code", p)} {...rest(p)} />;

export function Alert({ variant = "default", ...p }: El<"div"> & { variant?: "default" | "destructive" }) {
  return <div data-slot="alert" role="alert" data-variant={variant} className={cls("ui-alert", p)} {...rest(p)} />;
}
export const AlertTitle = (p: El<"div">) => <div data-slot="alert-title" className={cls("ui-alert-title", p)} {...rest(p)} />;
export const AlertDescription = (p: El<"div">) => <div data-slot="alert-description" className={cls("ui-alert-description", p)} {...rest(p)} />;

export const Skeleton = (p: El<"div">) => <div data-slot="skeleton" className={cls("ui-skeleton", p)} {...rest(p)} />;

export function Table(p: El<"table">) {
  return <div data-slot="table-container" className="ui-table-wrap"><table data-slot="table" className={cls("ui-table", p)} {...rest(p)} /></div>;
}
export const TableHeader = (p: El<"thead">) => <thead data-slot="table-header" {...p} />;
export const TableBody = (p: El<"tbody">) => <tbody data-slot="table-body" {...p} />;
export const TableRow = (p: El<"tr">) => <tr data-slot="table-row" {...p} />;
export const TableHead = (p: El<"th">) => <th data-slot="table-head" {...p} />;
export const TableCell = (p: El<"td">) => <td data-slot="table-cell" {...p} />;
export const TableCaption = (p: El<"caption">) => <caption data-slot="table-caption" {...p} />;

// shadcn's Breadcrumb: a trail of links, the last the page you're on.
// BreadcrumbLink takes asChild, so a step can be a button.
export const Breadcrumb = (p: El<"nav">) => <nav data-slot="breadcrumb" aria-label="breadcrumb" className={cls("ui-breadcrumb", p)} {...rest(p)} />;
export const BreadcrumbList = (p: El<"ol">) => <ol data-slot="breadcrumb-list" className={cls("ui-breadcrumb-list", p)} {...rest(p)} />;
export const BreadcrumbItem = (p: El<"li">) => <li data-slot="breadcrumb-item" className={cls("ui-breadcrumb-item", p)} {...rest(p)} />;
export function BreadcrumbLink({ asChild, ...p }: El<"a"> & { asChild?: boolean }) {
  const Comp: any = asChild ? Slot.Root : "a";
  return <Comp data-slot="breadcrumb-link" className={asChild ? slotted("ui-breadcrumb-link", p) : cls("ui-breadcrumb-link", p)} {...rest(p)} />;
}
export const BreadcrumbPage = (p: El<"span">) => <span data-slot="breadcrumb-page" role="link" aria-disabled="true" aria-current="page" className={cls("ui-breadcrumb-page", p)} {...rest(p)} />;
export const BreadcrumbSeparator = ({ children, ...p }: El<"li">) => (
  <li data-slot="breadcrumb-separator" role="presentation" aria-hidden="true" className={cls("ui-breadcrumb-separator", p)} {...rest(p)}>{children ?? <ChevronRight />}</li>
);

export const ScrollArea =(p: El<"div"> & { children?: ComponentChildren }) => <div data-slot="scroll-area" className={cls("ui-scroll", p)} {...rest(p)} />;

// ResizeHandle: shadcn's Resizable reduced to its handle, which sizes the
// panel beside it. It writes `size` px to `name`, a CSS property of its
// parent, by the CSSOM (the page's CSP refuses a style attribute), so the
// parent's grid reads it. A drag, the arrows (Shift for larger steps),
// Home and End move it; a double click or Enter goes back to `initial`.
// onSize(px, done) is told each step, done once the drag ends. The one
// component here with handlers: drawn ahead it is a hairline, and works once
// the page hydrates. It carries no Radix.
export function ResizeHandle({ name, size, min, max, initial, onSize, side = "end", ...p }: Omit<El<"div">, "onSize"> & {
  name: string; size: number; min: number; max: number; initial: number; onSize: (px: number, done: boolean) => void; side?: "start" | "end";
}) {
  const el = useRef<HTMLDivElement>(null);
  const clamp = (n: number) => Math.round(Math.min(max, Math.max(min, n)));
  useLayoutEffect(() => { el.current?.parentElement?.style.setProperty(name, `${clamp(size)}px`); });
  const dir = side === "end" ? 1 : -1;
  const down = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const t = e.currentTarget as HTMLElement, x0 = e.clientX, s0 = size;
    let now = s0;
    t.setPointerCapture(e.pointerId);
    t.dataset.dragging = "";
    const move = (m: PointerEvent) => { now = clamp(s0 + dir * (m.clientX - x0)); el.current?.parentElement?.style.setProperty(name, `${now}px`); onSize(now, false); };
    const up = () => { t.removeEventListener("pointermove", move); t.removeEventListener("pointerup", up); t.removeEventListener("pointercancel", up); delete t.dataset.dragging; onSize(now, true); };
    t.addEventListener("pointermove", move);
    t.addEventListener("pointerup", up);
    t.addEventListener("pointercancel", up);
    e.preventDefault();
  };
  const key = (e: KeyboardEvent) => {
    const step = e.shiftKey ? 64 : 16;
    const to = e.key === "ArrowRight" ? size + dir * step : e.key === "ArrowLeft" ? size - dir * step : e.key === "Home" ? min : e.key === "End" ? max : e.key === "Enter" ? initial : null;
    if (to === null) return;
    e.preventDefault();
    onSize(clamp(to), true);
  };
  return (
    <div ref={el} role="separator" aria-orientation="vertical" aria-valuenow={clamp(size)} aria-valuemin={min} aria-valuemax={max} tabIndex={0}
      data-slot="resize-handle" data-side={side} className={cls("ui-resize-handle", p)} {...rest(p)}
      onPointerDown={down} onKeyDown={key} onDblClick={() => onSize(clamp(initial), true)} />
  );
}
